import { canMeasureGoogle, safePage } from './googleMeasurementConsent.js';
import { syncMeasurementConsent } from './measurement.js';

// Keep the external feature's labels intact. Direct Ads routing is separate
// from the existing GA4 consultation / qualified-call measurement.
export const GOOGLE_ADS_ID = 'AW-18550821750';
export const ADS_CONVERSIONS = Object.freeze({
  lead_form: 'AW-18550821750/FLSOCPLaqbAdEPam3I1F',
  phone_call: 'AW-18550821750/kwhnCOiXobAdEPam3I1F',
  signup: 'AW-18550821750/HXm3CPqXobAdEPam3I1F',
});
// The owner explicitly retains both destinations, behind the existing gate.
export const DIRECT_GOOGLE_ADS_ENABLED = true;
const LABELS = new Set(Object.values(ADS_CONVERSIONS));
const EVENT_ID = /^evt_[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function pageContext() {
  const page = safePage(window.location.pathname + (window.location.search || ''));
  return page ? {
    page_location: page,
    page_referrer: '',
    page_title: 'Trinity Dental Arts',
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    allow_enhanced_conversions: false,
  } : null;
}

export function createGoogleAdsConversionClient({ enabled = false } = {}) {
  let configuredWindow = null;
  function initializeGoogleAds() {
    try {
      if (!enabled || typeof window === 'undefined' || !canMeasureGoogle() ||
          typeof window.gtag !== 'function') return false;
      const context = pageContext();
      if (!context || !syncMeasurementConsent()) return false;
      // Reuse the existing consent-aware GTM/Google-tag loader and queue.
      // A gtag stub alone is not evidence of delivered measurement.
      if (configuredWindow !== window) {
        window.gtag('config', GOOGLE_ADS_ID, { send_page_view: false, ...context });
        configuredWindow = window;
      }
      return true;
    } catch { return false; }
  }
  function fireAdsConversion(sendTo, params = {}) {
    try {
      // Blocked means no config, conversion enqueue, script or optional write.
      if (!enabled || !LABELS.has(sendTo) || !canMeasureGoogle() ||
          !initializeGoogleAds()) return false;
      const context = pageContext();
      if (!context) return false;
      // syncMeasurementConsent above updates current consent before this event.
      const payload = { send_to: sendTo, ...context };
      if (typeof params?.transaction_id === 'string' && EVENT_ID.test(params.transaction_id)) {
        payload.transaction_id = params.transaction_id;
      }
      window.gtag('event', 'conversion', payload);
      return true;
    } catch { return false; }
  }
  return { initializeGoogleAds, fireAdsConversion };
}

const client = createGoogleAdsConversionClient({ enabled: DIRECT_GOOGLE_ADS_ENABLED });
export const initializeGoogleAds = () => client.initializeGoogleAds();
export const fireAdsConversion = (sendTo, params) => client.fireAdsConversion(sendTo, params);

// Share observations across component remounts in this document. No blocked
// observation is persisted or queued; a full reload starts a fresh document.
const observedSignupUsers = new WeakMap();
export function createSignupConversionReporter({
  enabled = DIRECT_GOOGLE_ADS_ENABLED,
  fire = fireAdsConversion,
} = {}) {
  let observed = new Set();
  if (typeof window !== 'undefined') {
    if (!observedSignupUsers.has(window)) observedSignupUsers.set(window, observed);
    observed = observedSignupUsers.get(window);
  }
  return user => {
    if (!user?.id || observed.has(user.id)) return false;
    observed.add(user.id);
    try {
      if (!enabled || !canMeasureGoogle()) return false;
      const created = String(user.created_date || '');
      const date = /(?:Z|[+-]\d{2}:?\d{2})$/.test(created) ? created : created + 'Z';
      const age = Date.now() - Date.parse(date);
      if (!Number.isFinite(age) || age < 0 || age >= 24 * 60 * 60 * 1000) return false;
      const key = '_aw_signup_fired_' + ADS_CONVERSIONS.signup + '_' + user.id;
      if (localStorage.getItem(key)) return false;
      const uuid = globalThis.crypto?.randomUUID?.();
      const eventId = 'evt_' + (uuid || '');
      if (!EVENT_ID.test(eventId)) return false;
      // Retain the existing first-party dedupe key; no user ID leaves the page.
      localStorage.setItem(key, eventId);
      if (fire(ADS_CONVERSIONS.signup, { transaction_id: eventId })) return true;
      if (localStorage.getItem(key) === eventId) localStorage.removeItem(key);
      return false;
    } catch { return false; }
  };
}

